import React from 'react';

/**
 * Linkify — render a string with embedded URLs as React text + anchor nodes.
 *
 * Drop-in replacement for `{text}` anywhere we display user-typed prose
 * (event descriptions, Signal text, etc). Detects http(s):// and bare
 * www.-prefixed URLs, leaves the surrounding text alone, and renders each
 * URL as a target=_blank rel=noopener anchor.
 *
 * We avoid dangerouslySetInnerHTML so user-provided text can never inject
 * markup. Splitting the string with a single regex preserves order and
 * whitespace exactly as typed.
 */
const URL_RE = /(\bhttps?:\/\/[^\s<>"]+|\bwww\.[^\s<>"]+)/gi;

// Trailing punctuation that's almost never part of an intended URL — strip
// it from the match and put it back into the surrounding text. Otherwise
// "see https://example.com." would render the trailing period inside the link.
const TRAILING_PUNCT = /[.,;:!?)\]]+$/;

export default function Linkify({ text, className, style, linkClassName, linkStyle }) {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let match;
  // Reset lastIndex defensively in case of module-level regex re-entry.
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(text)) !== null) {
    const start = match.index;
    const raw = match[0];
    const trailing = (raw.match(TRAILING_PUNCT) || [''])[0];
    const url = trailing ? raw.slice(0, raw.length - trailing.length) : raw;
    const href = url.startsWith('www.') ? `https://${url}` : url;
    if (start > lastIndex) parts.push(text.slice(lastIndex, start));
    parts.push(
      <a
        key={`${start}-${url}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName || 'underline'}
        style={linkStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    );
    if (trailing) parts.push(trailing);
    lastIndex = start + raw.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  // Wrap in a fragment so the caller controls whitespace handling via
  // their own container (e.g. whitespace-pre-wrap).
  return (
    <span className={className} style={style}>
      {parts.map((p, i) => (typeof p === 'string' ? <React.Fragment key={i}>{p}</React.Fragment> : p))}
    </span>
  );
}
